import { createContext, useContext, useEffect, useMemo } from "react";
import en from "./en";
import de from "./de";
import fi from "./fi";
import romanizeAddress from "./address";

/**
 * 경로 접두사 기반 다국어.
 *   propmate.kr/        → 한국어 (basename "/")
 *   propmate.kr/en      → 영어   (basename "/en")
 *   propmate.kr/de      → 독일어 (basename "/de")
 *   propmate.kr/fi      → 핀란드어 (basename "/fi")
 *   propmate.kr/de/map  → 독일어 지도
 *
 * BrowserRouter의 basename으로 접두사를 떼기 때문에 App.js의 라우트 테이블과
 * 컴포넌트 안의 <Link to="/map">, navigate("/login") 은 손대지 않아도
 * 자동으로 현재 언어의 경로를 유지한다.
 *
 * 사전은 "한국어 원문 → 번역" 매핑이다. 찾는 순서는
 *   현재 언어 사전 → 영어 사전 → 한국어 원문
 * 이라서, 독일어·핀란드어 번역이 빠진 문구는 적어도 영어로 보인다.
 */

export const LANGS = ["ko", "en", "de", "fi"];
export const DEFAULT_LANG = "ko";
/** 토글에 표시할 짧은 이름 */
export const LANG_LABELS = { ko: "KO", en: "EN", de: "DE", fi: "FI" };
/** 언어 선택 팝업에 표시할 이름. 어느 화면에서든 그 언어 자신의 표기로 보여 준다. */
export const LANG_NAMES = { ko: "한국어", en: "English", de: "Deutsch", fi: "Suomi" };

const DICTS = { en, de, fi };
const PREFIXED = LANGS.filter((l) => l !== DEFAULT_LANG);
const PREFIX_RE = new RegExp(`^/(${PREFIXED.join("|")})(?=/|$)`, "i");

// Intl 포맷용 로케일과 og:locale 값
const LOCALES = { ko: "ko-KR", en: "en-US", de: "de-DE", fi: "fi-FI" };
const OG_LOCALES = { en: "en_US", de: "de_DE", fi: "fi_FI" };

/** 현재 pathname에서 언어와 BrowserRouter에 넘길 basename을 뽑는다. */
export function detectLang(pathname) {
    const matched = PREFIX_RE.exec(pathname || "");
    // basename은 실제로 매칭된 문자열을 그대로 써야 대소문자가 섞여도 접두사가 떨어진다.
    return matched
        ? { lang: matched[1].toLowerCase(), basename: matched[0] }
        : { lang: DEFAULT_LANG, basename: "/" };
}

/** 언어별 접두사. 한국어는 접두사가 없다. */
export function langPrefix(lang) {
    return lang && lang !== DEFAULT_LANG ? `/${lang}` : "";
}

/**
 * 라우터를 거치지 않는 이동(window.location 직접 대입 등)에 쓸 절대 경로.
 * langHref("/login", "de") → "/de/login"
 */
export function langHref(path, lang) {
    const clean = path.startsWith("/") ? path : `/${path}`;
    return `${langPrefix(lang)}${clean}` || "/";
}

// 개발 중 번역 누락을 한 번씩만 알려준다. 운영 빌드에서는 동작하지 않는다.
const warned = new Set();
function warnMissing(lang, text) {
    if (process.env.NODE_ENV !== "development") return;
    const key = `${lang}:${text}`;
    if (warned.has(key)) return;
    warned.add(key);
    console.warn(`[i18n] ${lang} 번역 누락: ${JSON.stringify(text)}`);
}

/** t("총 {n}건", { n: 3 }) 처럼 중괄호 자리표시자를 채운다. */
function interpolate(template, vars) {
    if (!vars) return template;
    return template.replace(/\{(\w+)\}/g, (whole, name) =>
        Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : whole
    );
}

const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

export function translate(lang, text, vars) {
    if (typeof text !== "string") return text;
    const dict = DICTS[lang];
    if (!dict) return interpolate(text, vars); // 한국어
    if (has(dict, text)) return interpolate(dict[text], vars);
    warnMissing(lang, text);
    // 독일어·핀란드어에 없으면 영어라도 보여 준다.
    if (lang !== "en" && has(DICTS.en, text)) return interpolate(DICTS.en[text], vars);
    return interpolate(text, vars);
}

/**
 * 훅을 쓸 수 없는 모듈(유틸·비(非)컴포넌트 함수)용 번역.
 * 언어는 페이지 로드 시점에 경로 접두사로 한 번 정해지고 그 뒤 바뀌지 않으므로
 * 모듈 로드 시 한 번 판정해 두어도 안전하다.
 */
export const currentLang =
    typeof window !== "undefined"
        ? detectLang(window.location.pathname).lang
        : DEFAULT_LANG;

/** 한국어가 아닌 화면인가. 주소 로마자·금액 환산 등 "외국어 공통" 분기에 쓴다. */
export const isForeign = currentLang !== DEFAULT_LANG;

/**
 * 화면에 뿌릴 주소. 외국어일 때만 로마자 표기로 바꾼다.
 * 네이버 지오코더가 한국어 주소만 주기 때문에 필요하다.
 */
export function displayAddress(address) {
    return isForeign ? romanizeAddress(address) : address;
}

/*
 * 통화 기호는 Intl 의 currency 스타일에 맡기지 않는다. fi 로케일은 narrowSymbol 을 줘도
 * "KRW" 코드로 찍는 환경이 있어서, 숫자만 로케일로 포맷하고 ₩ 는 직접 붙인다.
 * 영어는 기호가 앞, 독일어·핀란드어는 뒤(띄어 씀)가 관례다.
 */
const withWon = (num) => (currentLang === "en" ? `₩${num}` : `${num} ₩`);
const numberFormat = (opts) => new Intl.NumberFormat(LOCALES[currentLang] || "en-US", opts);

/**
 * 숫자 자릿수 구분을 화면 언어에 맞춘다. ko·en: 3,695,000 / de: 3.695.000 / fi: 3 695 000
 * (독일어에서 쉼표는 소수점이라 3,695,000 은 오독될 수 있다.)
 */
export function formatNumber(n) {
    return numberFormat({ maximumFractionDigits: 0 }).format(n);
}

/** 원화 금액 전체 표기. en: ₩1,234,567 / de: 1.234.567 ₩ / fi: 1 234 567 ₩ */
export function formatWon(won) {
    return withWon(numberFormat({ maximumFractionDigits: 0 }).format(won));
}

/**
 * 원화 금액 축약 표기. 억 단위는 외국어권에 통하지 않아 로케일의 축약 단위로 바꾼다.
 * en: ₩1.29B·₩387M / de: 1,29 Mrd. ₩ / fi: 387 milj. ₩
 */
export function formatWonCompact(won) {
    // 유효숫자 3자리: 386,57 milj. → 387 milj. / 1,29 mrd. 그대로. 지도 말풍선(90px 고정)에 들어가야 한다.
    return withWon(numberFormat({ notation: "compact", maximumSignificantDigits: 3 }).format(won));
}

export function tStatic(text, vars) {
    return translate(currentLang, text, vars);
}

const I18nContext = createContext({
    lang: DEFAULT_LANG,
    t: (text, vars) => interpolate(text, vars),
    langHref: (path) => path,
});

// 외국어 진입 시 교체할 문서 메타. index.html은 정적이라 런타임에 바꿔준다.
const DOC_META = {
    en: "PROPMATE is a map-based platform that brings every piece of land information together — zoning, regulations, buildable scale and construction cost — in one place.",
    de: "PROPMATE ist eine kartenbasierte Plattform, die alle Grundstücksinformationen an einem Ort bündelt – Flächennutzung, Vorschriften, bebaubare Größe und Baukosten.",
    fi: "PROPMATE on karttapohjainen palvelu, joka kokoaa kaikki tonttitiedot yhteen paikkaan – kaavoituksen, määräykset, rakennusoikeuden ja rakennuskustannukset.",
};

function setMeta(selector, value) {
    const el = document.head.querySelector(selector);
    if (el) el.setAttribute("content", value);
}

export function I18nProvider({ lang = DEFAULT_LANG, children }) {
    const value = useMemo(
        () => ({
            lang,
            t: (text, vars) => translate(lang, text, vars),
            langHref: (path) => langHref(path, lang),
        }),
        [lang]
    );

    useEffect(() => {
        document.documentElement.lang = lang;
        const description = DOC_META[lang];
        if (!description) return; // 한국어는 index.html 원본 값을 그대로 둔다.
        document.title = "PROPMATE";
        setMeta('meta[name="description"]', description);
        setMeta('meta[property="og:description"]', description);
        setMeta('meta[name="twitter:description"]', description);
        setMeta('meta[property="og:locale"]', OG_LOCALES[lang]);
    }, [lang]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
    return useContext(I18nContext);
}

/** 가장 많이 쓰는 형태: const t = useT(); t("지도 보러가기") */
export function useT() {
    return useContext(I18nContext).t;
}
